/** Theme constants for the application
 * Centralized location for colors, button styles, and other design tokens
 */

// Button Colors
export const buttonColors = {
    primary: '#0d4a63',       // PrimaryBlueColor - Used in multiple components
    secondary: '#8b4513',     // Brown - Used in CustomerHomePage icons
    success: '#4caf50',       // GreenBtnColor - Used in multiple components
    danger: '#dc3545',        // Red
    dangerAlt: '#d33',        // Cancel button color - Used in Layout components
    warning: '#ffc107',       // Yellow/Orange
    info: '#0dcaf0',          // Cyan
    light: '#f8f9fa',         // Light gray
    dark: '#212529',          // Dark gray/black
} as const;

// Brand Colors (from CSS variables and actual usage)
export const brandColors = {
    navyBlue: '#0d537b',
    royalBlue: '#43a0db',
    orangeCustom: '#FF7130',
    purpleCustom: '#70277D',
    blue100: '#02A4E3',       // FM-Blue-100 - Used extensively in confirm buttons, SVGs
    lightBlue: '#2099d2',     // Used in buttons, tabs, backgrounds
    darkBlue: '#1a7fb3',      // Used as hover state for lightBlue
    green: '#22c574',         // Used in active-btn-green CSS class
    red: '#f96726',           // Used in active-btn-red CSS class
    orange: '#ff7b00',        // Used as OrangeBtnColor in multiple components
} as const;

// Neutral Colors
export const neutralColors = {
    white: '#ffffff',
    black: '#000000',
    gray: '#6c757d',
    grayLight: '#e3e3e3',
    grayDark: '#343a40',
    lightGray: '#d5d5d5',
    darkGray: '#333',           // Used in text colors
    darkBlueGray: '#132D3D',    // Used in LinearProgress backgrounds
    darkBlueGrayAlt: '#162f3c', // Used in CSS (similar to darkBlueGray)
} as const;

// Button Variant Color Map (for backward compatibility)
export const variantColorMap = {
    primary: buttonColors.primary,
    secondary: buttonColors.secondary,
    success: buttonColors.success,
    danger: buttonColors.danger,
    warning: buttonColors.warning,
    info: buttonColors.info,
} as const;

// Button Styles (from actual CSS usage)
export const buttonStyles = {
    borderRadius: {
        default: '40px',        // Used in login/submit buttons
        rounded: '100px',       // Used extensively in active-btn-* classes
        square: '10px',
        circle: '100%',         // Used in some components
    },
    padding: {
        small: '6px 0px',              // Used in active-btn-blue
        smallHorizontal: '6px 12px',
        medium: '10px 0px',            // Used in active-btn-green/red
        mediumHorizontal: '10px 20px',
        large: '16px 32px',            // Used in submit buttons
        largeHorizontal: '10px 41px',  // Used in next-btn, cancel-btn
    },
    fontSize: {
        small: '14px',
        medium: '15px',        // Used in active-btn-* classes
        large: '18px',         // Used in submit buttons
    },
    minWidth: {
        small: '125px',        // Used in active-btn-* classes
        medium: '145px',       // Used in active-btn-* classes
    },
    maxHeight: {
        button: '45px',        // Used in active-btn-* classes
    },
} as const;

// Export all colors as a single object for convenience
export const colors = {
    button: buttonColors,
    brand: brandColors,
    neutral: neutralColors,
} as const;

// Type exports for TypeScript
export type ButtonColor = keyof typeof buttonColors;
export type ButtonVariant = keyof typeof variantColorMap;


// Dashboard Tile Colors
export const LightBlueBtnColor = '#2099d2';  // rgb(32, 153, 210)
export const DarkBlueBtnColor = '#1a7fb3';  // rgb(26, 127, 179)
export const HoverLightBlueBtnColor = '#1a7fb3';  // rgb(26, 127, 179)
export const HoverUnselectedLightBlueBtnColor = 'rgba(32, 153, 210, 0.1)';  // rgba(32, 153, 210, 0.1)
export const GreyBtnColor = '#f5f5f5';
export const WhiteColor = '#fff';
// Popup modal colors
export const GreenBtnColor = '#4caf50';
export const OrangeBtnColor = '#ff7b00';
export const PrimaryBlueColor = '#0d4a63';
